// PreventiApp · Sprint 3 · Etapa 7 — Detalle de observación + cambio de estado.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ChevronLeft } from 'lucide-react';
import {
  getObservacion, listVersionesObs, cambiarEstadoObservacion, getFirmaFoto, listProfileNames,
} from '../../data/supabaseAdmin';
import { CONDUCTS, DOMAINS, REGISTRO_ESTADOS, VALUE_LABELS } from '../../../src/data/observationEngine';

const conductaLabel = (code) => {
  if (!code) return '—';
  const dom = code.split('-')[0];
  return (CONDUCTS[dom] || []).find((c) => c.code === code)?.label || code;
};

// Transiciones de estado permitidas para encargado_convivencia (capa visual; RLS valida en DB)
const TRANSICIONES = {
  registrado: ['en_revision', 'cerrado'],
  editado: ['en_revision', 'cerrado'],
  en_revision: ['en_seguimiento', 'cerrado'],
  en_seguimiento: ['cerrado'],
};

export default function AdminObservacionDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [obs, setObs] = useState(null);
  const [versiones, setVersiones] = useState([]);
  const [fotos, setFotos] = useState([]);
  const [autor, setAutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reload = async () => {
    setLoading(true); setError('');
    try {
      const o = await getObservacion(id);
      setObs(o);
      const [vs, names] = await Promise.all([listVersionesObs(id), listProfileNames([o.autor_id])]);
      setVersiones(vs);
      setAutor(names[o.autor_id] || null);
      // Signed URLs para fotos
      const fotosE = (o.observaciones_evidencias || []).filter((e) => e.tipo === 'foto');
      const signedFotos = await Promise.all(fotosE.map(async (e) => ({
        ...e, signedUrl: await getFirmaFoto(e.storage_path).catch(() => null),
      })));
      setFotos(signedFotos);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, [id]);

  const puedeCambiarEstado = profile?.rol === 'encargado_convivencia' && obs && profile.colegio_id === obs.colegio_id;
  const transiciones = obs ? (TRANSICIONES[obs.estado] || []) : [];

  const onCambiarEstado = async (nuevo) => {
    if (!window.confirm(`¿Cambiar estado a "${REGISTRO_ESTADOS[nuevo]?.label || nuevo}"?`)) return;
    setBusy(true); setError('');
    try {
      await cambiarEstadoObservacion(id, nuevo);
      await reload();
    } catch (e) { setError(`No se pudo cambiar el estado: ${e.message}`); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-8" data-testid="obs-detalle-loading">Cargando…</div>;
  if (error && !obs) return <div className="p-8 text-[#991B1B]" data-testid="obs-detalle-error">{error}</div>;
  if (!obs) return null;

  const textos = (obs.observaciones_evidencias || []).filter((e) => e.tipo === 'texto');
  const dominio = DOMAINS.find((d) => d.id === obs.dominio_code);

  return (
    <div className="p-8 max-w-5xl" data-testid="page-obs-detalle">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs font-bold text-[#5346E6] hover:underline" data-testid="obs-detalle-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Volver
        </button>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F4F7FE] font-mono text-[#475472]">{obs.id.slice(0,8)}…</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white border border-[#E5EAF5] rounded-2xl p-6">
          <p className="text-xs font-bold text-[#475472] uppercase">{dominio?.name || obs.dominio_code}</p>
          <h1 className="mt-1 text-2xl font-extrabold text-[#0B1B5E]" data-testid="obs-detalle-conducta">{conductaLabel(obs.conducta_code)}</h1>
          <p className="mt-1 text-xs text-[#475472]">
            {obs.modo_registro === 'induccion' ? '🟨 Modo Inducción' : 'Registro Oficial'} · Flujo {obs.flujo_captura}
          </p>

          <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
            <Field label="Curso" value={obs.curso_texto || '—'} />
            <Field label="Estudiante" value={obs.estudiante || '—'} />
            <Field label="Contexto" value={VALUE_LABELS.contexto?.[obs.contexto] || obs.contexto || '—'} />
            <Field label="Frecuencia" value={VALUE_LABELS.frecuencia?.[obs.frecuencia] || obs.frecuencia || '—'} />
            <Field label="Intensidad" value={VALUE_LABELS.intensidad?.[obs.intensidad] || obs.intensidad || '—'} />
            <Field label="Duración" value={VALUE_LABELS.duracion?.[obs.duracion] || obs.duracion || '—'} />
            <Field label="Participantes" value={VALUE_LABELS.participantes?.[obs.participantes] || obs.participantes || '—'} />
            <Field label="Fuente" value={VALUE_LABELS.fuente?.[obs.fuente] || obs.fuente || '—'} />
            <Field label="Autor" value={autor?.nombre_completo || obs.autor_id} />
            <Field label="Creado" value={new Date(obs.creado_en).toLocaleString('es-CL')} />
          </div>

          {textos.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-bold text-[#0B1B5E] uppercase">Nota de evidencia</p>
              {textos.map((t) => (
                <p key={t.id} className="mt-2 text-sm text-[#0B1B5E] bg-[#F4F7FE] rounded-xl p-3" data-testid={`obs-detalle-texto-${t.id.slice(0,6)}`}>{t.contenido_texto}</p>
              ))}
            </div>
          )}

          {fotos.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-bold text-[#0B1B5E] uppercase">Fotos ({fotos.length}) <span className="font-normal text-[#475472] normal-case">— signed URLs, expiran en 5 min</span></p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {fotos.map((f, i) => (
                  <div key={f.id} className="aspect-square rounded-xl overflow-hidden bg-[#F4F7FE] border border-[#DDE6F7]" data-testid={`obs-detalle-foto-${i}`}>
                    {f.signedUrl ? (
                      <img src={f.signedUrl} alt={`Evidencia ${i+1}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] text-[#991B1B]">URL no disponible</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-[#E5EAF5] rounded-2xl p-5">
            <p className="text-xs font-bold text-[#475472] uppercase">Estado actual</p>
            <p className="mt-1 text-base font-extrabold text-[#0B1B5E]" data-testid="obs-detalle-estado">{REGISTRO_ESTADOS[obs.estado]?.label || obs.estado}</p>
            {puedeCambiarEstado && transiciones.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="obs-detalle-transiciones">
                <p className="text-[11px] font-bold text-[#475472]">Cambiar a:</p>
                {transiciones.map((t) => (
                  <button key={t} disabled={busy} onClick={() => onCambiarEstado(t)} data-testid={`obs-trans-${t}`}
                          className="w-full text-left px-3 py-2 rounded-lg text-xs font-bold bg-[#EEF0FE] hover:bg-[#5346E6] hover:text-white text-[#5346E6] disabled:opacity-60">
                    → {REGISTRO_ESTADOS[t]?.label || t}
                  </button>
                ))}
              </div>
            )}
            {!puedeCambiarEstado && (
              <p className="mt-2 text-[11px] text-[#475472]">Sólo el Encargado de Convivencia del colegio puede cambiar el estado.</p>
            )}
            {error && <p className="mt-2 text-[11px] text-[#991B1B]" data-testid="obs-detalle-trans-error">{error}</p>}
          </div>

          <div className="bg-white border border-[#E5EAF5] rounded-2xl p-5">
            <p className="text-xs font-bold text-[#475472] uppercase mb-2">Historial ({versiones.length})</p>
            <ul className="space-y-2 text-xs max-h-72 overflow-y-auto" data-testid="obs-detalle-historial">
              {versiones.map((v) => (
                <li key={v.id} className="border-l-2 border-[#5346E6]/40 pl-2">
                  <p className="font-bold text-[#0B1B5E]">{v.accion}</p>
                  <p className="text-[10px] text-[#475472]">{new Date(v.creado_en).toLocaleString('es-CL')}</p>
                </li>
              ))}
              {versiones.length === 0 && <li className="text-[#475472]">Sin versiones registradas.</li>}
            </ul>
          </div>
        </div>
      </div>

      <Link to="/admin/observaciones" className="mt-6 inline-block text-xs font-bold text-[#5346E6] hover:underline" data-testid="obs-detalle-back-list">← Volver a la lista</Link>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-bold text-[#475472] uppercase">{label}</p>
    <p className="text-[#0B1B5E]">{value}</p>
  </div>
);
